import { useEffect, useState, useCallback } from "react";
import ConfirmPasswordfiled from "@/app/components/common/actions/inputs-b/ConfirmPasswordfiled";
import PasswordFiled from "@/app/components/common/actions/inputs-b/PasswordFiled";
import OldPasswordFiled from "@/app/components/common/actions/inputs-b/OldPasswordFiled";
import { gxChangepasswordApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import DialogTechnical from "@/app/models/dialogs/TechnicalError/DialogTechnical";
import DialogSuccessfully from "@/app/models/dialogs/TechnicalError/DialogSuccessfully";
import { getEnvConfig } from "@/app/apiConfig/urlConfig/envConfig";
import {
  resolvePasswordError,
  type PasswordErrorEntry,
} from "./passwordErrorCodes";

interface Props {
  setchangepassword: React.Dispatch<React.SetStateAction<() => void>>;
  onclose: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ChangePassword({
  setchangepassword,
  onclose,
}: Readonly<Props>) {
  const [oldPassword, setOldPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [triggerValidation, setTriggerValidation] = useState<boolean>(false);
  const [isErrorOpen, setErrorOpen] = useState<boolean>(false);
  const [isSuccessOpen, setSuccessOpen] = useState<boolean>(false);

  const [oldPasswordError, setOldPasswordError] = useState<string>("");
  const [newPasswordError, setNewPasswordError] = useState<string>("");
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");

  const { clientId } = getEnvConfig();

  const clearApiErrors = () => {
    setOldPasswordError("");
    setNewPasswordError("");
    setConfirmPasswordError("");
  };

  const isFormValid = useCallback((): boolean => {
    const trimmedOld = oldPassword.trim();
    const trimmedNew = newPassword.trim();
    const trimmedConfirm = confirmPassword.trim();

    if (!trimmedOld || trimmedOld.length < 8) return false;
    if (!trimmedNew || trimmedNew.length < 8) return false;
    if (!trimmedConfirm || trimmedConfirm.length < 8) return false;
    if (trimmedOld === trimmedNew) {
      setNewPasswordError(
        "New password must be different from the current password.",
      );
      return false;
    }
    if (trimmedNew !== trimmedConfirm) return false;

    return true;
  }, [oldPassword, newPassword, confirmPassword]);

  const handleSubmit = useCallback(async () => {
    clearApiErrors();
    setTriggerValidation(true);
    if (!isFormValid()) return;

    const payload = {
      oldPassword: oldPassword.trim(),
      newPassword: newPassword.trim(),
      confirmNewPassword: confirmPassword.trim(),
      clientId,
    };

    try {
      const response = await gxChangepasswordApi(payload);

      if (response?.status === 0) {
        const mapped: PasswordErrorEntry | null = resolvePasswordError(
          response?.errorCode,
        );

        if (mapped) {
          switch (mapped.field) {
            case "old":
              setOldPasswordError(mapped.message);
              break;
            case "new":
              setNewPasswordError(mapped.message);
              break;
            case "confirm":
              setConfirmPasswordError(mapped.message);
              break;
            default:
              setErrorOpen(true);
          }
        } else {
          setErrorOpen(true);
        }
        return;
      }

      if (response?.status === 1) {
        setSuccessOpen(true);
      }
    } catch (error) {
      console.error("Change password error:", error);
      setErrorOpen(true);
    }
  }, [oldPassword, newPassword, confirmPassword, clientId, isFormValid]);

  const handleforgotpassword = () => {};

  useEffect(() => {
    if (triggerValidation) {
      const timer = setTimeout(() => setTriggerValidation(false), 100);
      return () => clearTimeout(timer);
    }
  }, [triggerValidation]);

  useEffect(() => {
    setchangepassword(() => handleSubmit);
  }, [handleSubmit, setchangepassword]);

  return (
    <>
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <OldPasswordFiled
            label="Old Password"
            value={oldPassword}
            validationMessage={oldPasswordError}
            onChange={(val) => {
              setOldPassword(val);
              if (oldPasswordError) setOldPasswordError("");
              if (newPasswordError) setNewPasswordError("");
              if (triggerValidation) setTriggerValidation(false);
            }}
            triggerValidation={triggerValidation}
          />
        </div>

        <div className="col-span-12">
          <PasswordFiled
            label="New Password"
            value={newPassword}
            validationMessage={newPasswordError}
            onChange={(val) => {
              setNewPassword(val);
              if (newPasswordError) setNewPasswordError("");
              if (triggerValidation) setTriggerValidation(false);
            }}
            triggerValidation={triggerValidation}
          />
        </div>

        <div className="col-span-12">
          <ConfirmPasswordfiled
            label="Confirm Password"
            value={confirmPassword}
            validationMessage={confirmPasswordError}
            onChange={(val) => {
              setConfirmPassword(val);
              if (confirmPasswordError) setConfirmPasswordError("");
              if (triggerValidation) setTriggerValidation(false);
            }}
            matchValue={newPassword}
            triggerValidation={triggerValidation}
          />
        </div>
        {/* <div className="col-span-12">
          <div className="gx-forgot-password">
            <button onClick={handleforgotpassword}>Forgot Password</button>
          </div>
        </div> */}
      </div>

      {/* Error Dialog */}
      <DialogTechnical open={isErrorOpen} close={() => setErrorOpen(false)} />

      {/* Success Dialog */}
      <DialogSuccessfully
        text="Your password has been changed successfully."
        type="true"
        open={isSuccessOpen}
        close={() => {
          setSuccessOpen(false);
          onclose(false);
        }}
      />
    </>
  );
}
